import React from 'react';
import { motion } from 'framer-motion';

const PANELS = [
  {
    anchor: 'DOPE',
    headline: (
      <>
        We don&rsquo;t watermark video.<br />
        We <span className="text-teal">dope</span> it.
      </>
    ),
    body: (
      <>
        Every stream carries a unique, invisible, irremovable signature.
        It survives HDMI capture, re-encoding, compression, transcoding. Everything.
        <br />
        <br />
        Traditional watermarks can be detected and stripped.
        Our doping cannot be perceived, located, or removed.{' '}
        <span className="text-white">There is nothing to target.</span>
      </>
    ),
  },
  {
    anchor: 'IDENTIFY',
    headline: (
      <>
        Every pirate stream, traced to its{' '}
        <span className="text-teal">exact source.</span>
      </>
    ),
    body: (
      <>
        The individual subscriber account. The specific CDN token.
        The precise point in your distribution chain.
        <br />
        <br />
        Not after the event.{' '}
        <span className="text-white">During&nbsp;it.</span>
        <br />
        <br />
        Average detection time:{' '}
        <span className="text-teal font-semibold">4 seconds.</span>
      </>
    ),
  },
  {
    anchor: 'KILL',
    headline: (
      <>
        Source identified →{' '}
        <span className="text-teal">access revoked</span> →<br />
        pirate stream dies.
      </>
    ),
    body: (
      <>
        No takedown notice. No hosting provider.
        No court order. No waiting.
        <br />
        <br />
        <span className="text-white">
          The leak stops because you control the source.{' '}
          <span className="text-teal">Always have.</span>
        </span>
      </>
    ),
  },
  {
    anchor: 'ALWAYS ON',
    headline: (
      <>
        Pirates don&rsquo;t take shifts.<br />
        <span className="text-teal">Neither does Kinesight.</span>
      </>
    ),
    body: (
      <>
        Kinesight doesn&rsquo;t activate for match day and switch off at the final
        whistle. It runs <span className="text-white">continuously.</span>
        <br />
        <br />
        Every stream. Every session. Every second. The doping is embedded from
        the moment content enters your distribution chain and persists for the
        entire lifecycle of that stream.
        <br />
        <br />
        No protection window. No gap between events. No warm-up, no manual
        activation, no war room.
        <br />
        <br />
        <span className="text-white">
          Always doping. Always watching.{' '}
          <span className="text-teal">Always enforcing.</span>
        </span>
      </>
    ),
  },
];

const ECG_PATH = [
  'M 0 20',
  'Q 8 17 16 20 Q 20 23 24 20 L 28 23 L 32 2 L 36 36 L 40 20 Q 50 12 60 20 Q 70 22 80 19 Q 90 17 100 20',
  'Q 108 17 116 20 Q 120 23 124 20 L 128 23 L 132 2 L 136 36 L 140 20 Q 150 12 160 20 Q 170 22 180 19 Q 190 17 200 20',
  'Q 208 17 216 20 Q 220 23 224 20 L 228 23 L 232 2 L 236 36 L 240 20 Q 250 12 260 20 Q 270 22 280 19 Q 290 17 300 20',
  'Q 308 17 316 20 Q 320 23 324 20 L 328 23 L 332 2 L 336 36 L 340 20 Q 350 12 360 20 Q 370 22 380 19 Q 390 17 400 20',
].join(' ');

const AlwaysOnPulse = () => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay: 0.4 }}
    className="pt-6 space-y-4"
  >
    <div className="flex items-center gap-4">
      <span className="relative flex h-3 w-3">
        <span className="absolute inline-flex h-full w-full rounded-full bg-teal/50 animate-ping" />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-teal" />
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.35em] text-teal">
        24 / 7 / 365 · never flatlines
      </span>
    </div>
    <div className="relative h-14 w-full max-w-lg overflow-hidden">
      <div
        className="pointer-events-none absolute inset-y-1/2 left-0 right-0 h-px bg-teal/10"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10"
        style={{
          background:
            'linear-gradient(to right, rgba(11,17,32,1) 0%, rgba(11,17,32,0) 100%)',
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10"
        style={{
          background:
            'linear-gradient(to left, rgba(11,17,32,1) 0%, rgba(11,17,32,0) 100%)',
        }}
        aria-hidden
      />
      <motion.div
        className="absolute inset-y-0 left-0 flex h-full w-[200%]"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 7, ease: 'linear', repeat: Infinity }}
        aria-hidden
      >
        {[0, 1].map((i) => (
          <svg
            key={i}
            viewBox="0 0 400 40"
            preserveAspectRatio="none"
            className="h-full w-1/2 flex-shrink-0"
          >
            <path
              d={ECG_PATH}
              stroke="#00D4AA"
              strokeOpacity="0.2"
              strokeWidth="4"
              strokeLinejoin="round"
              strokeLinecap="round"
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
            <path
              d={ECG_PATH}
              stroke="#00D4AA"
              strokeWidth="1.6"
              strokeLinejoin="round"
              strokeLinecap="round"
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        ))}
      </motion.div>
    </div>
  </motion.div>
);

const IdentifyPulse = () => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true }}
    transition={{ delay: 0.4 }}
    className="flex items-center gap-4 pt-4"
  >
    <span className="relative flex h-3 w-3">
      <span className="absolute inline-flex h-full w-full rounded-full bg-teal/50 animate-ping" />
      <span className="relative inline-flex rounded-full h-3 w-3 bg-teal" />
    </span>
    <span className="font-mono text-xs uppercase tracking-[0.35em] text-teal">
      Live detection pulse · 4&nbsp;second cycle
    </span>
  </motion.div>
);

const Panel = ({ panel, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-15%' }}
      transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
      className="relative grid grid-cols-12 gap-6 py-20 md:py-28 border-t border-white/5"
    >
      <div className="col-span-12 md:col-span-5">
        <h3
          className="font-display font-bold text-teal tracking-tight"
          style={{ fontSize: 'clamp(3rem, 8vw, 6.5rem)', lineHeight: 0.9 }}
        >
          {panel.anchor}
        </h3>
      </div>

      <div className="col-span-12 md:col-span-7 md:pl-8 space-y-8 pt-2">
        <h4
          className="font-display font-semibold text-white leading-[1.05] tracking-tight"
          style={{ fontSize: 'clamp(1.75rem, 3.8vw, 3.4rem)' }}
        >
          {panel.headline}
        </h4>
        <p className="text-lg md:text-xl text-white/85 leading-[1.6] font-normal max-w-2xl">
          {panel.body}
        </p>
        {index === 1 && <IdentifyPulse />}
        {index === 3 && <AlwaysOnPulse />}
      </div>
    </motion.div>
  );
};

const Section04Approach = () => {
  return (
    <section className="section-pad section-glass" id="approach">
      <div className="container-edge">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col gap-6 mb-20 md:mb-28 max-w-5xl"
        >
          <h2 className="font-display font-bold text-white leading-[0.92] tracking-tight text-section">
            Stop chasing.<br />
            <span className="text-teal">Start owning the source.</span>
          </h2>
          <p className="editorial-body max-w-3xl">
            Kinesight inverts the enforcement model. Four capabilities.
            One continuous system. Zero downstream chasing, zero downtime.
          </p>
        </motion.div>

        <div>
          {PANELS.map((panel, i) => (
            <Panel key={panel.anchor} panel={panel} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Section04Approach;
